import cuid from 'cuid'

/**
 * Converts a base64 data URL to a File object.
 *
 * Fetches the base64 image data, converts it to a blob, and creates a File object
 * with a generated filename. Useful for converting base64 images to files for upload.
 *
 * @param base64image - Base64 data URL string (e.g., "data:image/png;base64,...")
 * @returns Promise that resolves to a File object
 *
 * @example
 * ```typescript
 * const base64 = 'data:image/png;base64,iVBORw0KGgoAAAANSUhEUgAAAAEAAAABCAYAAAAfFcSJAAAADUlEQVR42mP8/5+hHgAHggJ/PchI7wAAAABJRU5ErkJggg=='
 * const file = await base64ToBlob(base64)
 * console.log(file.name) // 'image-cuid123.png'
 * console.log(file.type) // 'image/png'
 * ```
 */
export const base64ToBlob = async (base64image: string): Promise<File> => {
  const res = await fetch(base64image)
  const blob = await res.blob()
  const imageType = (blob.type.match(/(?:[^/])[^./]+$/i) || [])[0]
  const image = new File([blob], `image-${cuid()}.${imageType}`, {
    type: blob.type,
  })

  return image
}

/**
 * Converts a Blob or File to a base64 data URL string.
 *
 * Reads the file content and converts it to a base64-encoded data URL that can
 * be used directly in HTML img src attributes or for data transmission.
 *
 * @param file - Blob or File object to convert
 * @returns Promise that resolves to base64 data URL string, or null on error
 *
 * @example
 * ```typescript
 * const fileInput = document.querySelector('input[type="file"]')
 * const file = fileInput.files[0]
 * const base64 = await blobTobase64(file)
 * console.log(base64) // 'data:image/jpeg;base64,/9j/4AAQSkZJRgABAQEAYABgAAD...'
 *
 * // Use in img tag
 * document.querySelector('img').src = base64
 * ```
 */
export const blobTobase64 = async (
  file: Blob,
): Promise<null | string | ArrayBuffer> => {
  const result = await new Promise(
    (
      resolve: (
        result:
          | Promise<null | ArrayBuffer | string>
          | null
          | ArrayBuffer
          | string,
      ) => void,
      reject: (error?: any) => void,
    ) => {
      const reader = new FileReader()

      reader.onload = () => {
        resolve(reader.result)
      }
      reader.onerror = reject

      reader.readAsDataURL(file)
    },
  )

  return result
}
